import { useState } from 'react';

function ProfessionalInfo({ userData, onUpdateUserData, showNotification }) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    role: userData.role || '',
    company: userData.company || '',
    location: userData.location || '',
    skills: userData.skills || [],
    socialLinks: userData.socialLinks || {
      website: '',
      github: '',
      linkedin: '',
      twitter: ''
    }
  });
  const [newSkill, setNewSkill] = useState('');
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSocialLinkChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [name]: value
      }
    }));
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !form.skills.includes(newSkill.trim())) {
      setForm(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setForm(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Simple validation
    const newErrors = {};
    if (!form.role.trim()) newErrors.role = "El cargo es obligatorio";
    if (!form.company.trim()) newErrors.company = "La empresa es obligatoria";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    onUpdateUserData(form);
    setIsEditing(false);
    showNotification("Información profesional actualizada");
  };

  const handleCancel = () => {
    setForm({
      role: userData.role || '',
      company: userData.company || '',
      location: userData.location || '',
      skills: userData.skills || [],
      socialLinks: userData.socialLinks || {
        website: '',
        github: '',
        linkedin: '',
        twitter: ''
      }
    });
    setErrors({});
    setIsEditing(false);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <h3 className="text-lg font-medium text-white">Información Profesional</h3>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-[#ff1b6d] hover:text-[#ff1b6d]/80 focus:outline-none flex items-center"
          >
            <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
            <span>Editar</span>
          </button>
        )}
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Professional Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-400 mb-1">
                Cargo
              </label>
              <input
                type="text"
                id="role"
                name="role"
                value={form.role}
                onChange={handleChange}
                className={`w-full px-3 py-2 bg-[#252525] border ${
                  errors.role ? "border-red-500" : "border-[#3a3a3a]"
                } rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]`}
              />
              {errors.role && <p className="mt-1 text-sm text-red-500">{errors.role}</p>}
            </div>
            <div>
              <label htmlFor="company" className="block text-sm font-medium text-gray-400 mb-1">
                Empresa
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={form.company}
                onChange={handleChange}
                className={`w-full px-3 py-2 bg-[#252525] border ${
                  errors.company ? "border-red-500" : "border-[#3a3a3a]"
                } rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]`}
              />
              {errors.company && <p className="mt-1 text-sm text-red-500">{errors.company}</p>}
            </div>
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-400 mb-1">
              Ubicación
            </label>
            <input
              type="text"
              id="location"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Ej. Madrid, España"
              className="w-full px-3 py-2 bg-[#252525] border border-[#3a3a3a] rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]"
            />
          </div>

          {/* Skills */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Habilidades
            </label>
            <div className="mb-2">
              <div className="flex flex-wrap gap-2 mb-3">
                {form.skills.map((skill, index) => (
                  <div 
                    key={index} 
                    className="flex items-center bg-[#2c2c2c] text-white px-3 py-1 rounded-full"
                  >
                    <span className="mr-1">{skill}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill(skill)}
                      className="text-gray-400 hover:text-white focus:outline-none"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex">
                <input
                  type="text"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddSkill())}
                  placeholder="Añadir habilidad"
                  className="flex-grow px-3 py-2 bg-[#252525] border border-[#3a3a3a] rounded-l-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]"
                />
                <button
                  type="button"
                  onClick={handleAddSkill}
                  className="bg-[#3a3a3a] text-white px-3 py-2 rounded-r-md hover:bg-[#4a4a4a] focus:outline-none"
                >
                  Añadir
                </button>
              </div>
            </div>
          </div>

          {/* Social Links */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-3">
              Enlaces Profesionales
            </label>
            <div className="space-y-3">
              <div>
                <div className="flex items-center mb-1">
                  <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <label htmlFor="website" className="text-sm text-gray-400">Sitio Web</label>
                </div>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={form.socialLinks.website}
                  onChange={handleSocialLinkChange}
                  placeholder="https://tuwebsite.com"
                  className="w-full px-3 py-2 bg-[#252525] border border-[#3a3a3a] rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]"
                />
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                  </svg>
                  <label htmlFor="github" className="text-sm text-gray-400">GitHub</label>
                </div>
                <input
                  type="url"
                  id="github"
                  name="github"
                  value={form.socialLinks.github}
                  onChange={handleSocialLinkChange}
                  placeholder="https://github.com/username"
                  className="w-full px-3 py-2 bg-[#252525] border border-[#3a3a3a] rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]"
                />
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  <label htmlFor="linkedin" className="text-sm text-gray-400">LinkedIn</label>
                </div>
                <input
                  type="url"
                  id="linkedin"
                  name="linkedin"
                  value={form.socialLinks.linkedin}
                  onChange={handleSocialLinkChange}
                  placeholder="https://linkedin.com/in/username"
                  className="w-full px-3 py-2 bg-[#252525] border border-[#3a3a3a] rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]"
                />
              </div>
              <div>
                <div className="flex items-center mb-1">
                  <svg className="h-4 w-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                  </svg>
                  <label htmlFor="twitter" className="text-sm text-gray-400">Twitter</label>
                </div>
                <input
                  type="url"
                  id="twitter"
                  name="twitter"
                  value={form.socialLinks.twitter}
                  onChange={handleSocialLinkChange}
                  placeholder="https://twitter.com/username"
                  className="w-full px-3 py-2 bg-[#252525] border border-[#3a3a3a] rounded-md text-white focus:outline-none focus:ring-1 focus:ring-[#ff1b6d]"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex space-x-3">
            <button
              type="submit"
              className="px-4 py-2 bg-[#ff1b6d] text-white rounded-md hover:bg-[#ff1b6d]/90 focus:outline-none focus:ring-2 focus:ring-[#ff1b6d]/50"
            >
              Guardar Cambios
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-transparent border border-gray-600 text-gray-300 rounded-md hover:bg-[#252525] focus:outline-none"
            >
              Cancelar
            </button>
          </div>
        </form>
      ) : (
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="bg-[#252525] rounded-lg border border-[#3a3a3a] p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm text-gray-400 mb-1">Cargo</h4>
                <p className="text-white font-medium">{userData.role || 'No especificado'}</p>
              </div>
              <div>
                <h4 className="text-sm text-gray-400 mb-1">Empresa</h4>
                <p className="text-white font-medium">{userData.company || 'No especificado'}</p>
              </div>
            </div>
            {userData.location && (
              <div className="mt-3">
                <h4 className="text-sm text-gray-400 mb-1">Ubicación</h4>
                <div className="flex items-center text-white">
                  <svg className="h-4 w-4 text-[#ff1b6d] mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{userData.location}</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Skills */}
          {userData.skills && userData.skills.length > 0 && (
            <div className="bg-[#252525] rounded-lg border border-[#3a3a3a] p-4">
              <h4 className="text-sm text-gray-400 mb-3">Habilidades</h4>
              <div className="flex flex-wrap gap-2">
                {userData.skills.map((skill, index) => (
                  <span 
                    key={index} 
                    className="bg-[#2c2c2c] text-white px-3 py-1 rounded-full text-sm"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Social Links */}
          {userData.socialLinks && Object.values(userData.socialLinks).some(link => link) && (
            <div className="bg-[#252525] rounded-lg border border-[#3a3a3a] p-4">
              <h4 className="text-sm text-gray-400 mb-3">Enlaces Profesionales</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {userData.socialLinks.website && (
                  <a 
                    href={userData.socialLinks.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-300 hover:text-white transition-colors"
                  >
                    <svg className="h-5 w-5 text-[#ff1b6d] mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <span className="truncate">{userData.socialLinks.website}</span>
                  </a>
                )}
                
                {userData.socialLinks.github && (
                  <a 
                    href={userData.socialLinks.github}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-300 hover:text-white transition-colors"
                  >
                    <svg className="h-5 w-5 text-[#ff1b6d] mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                    <span className="truncate">GitHub</span>
                  </a>
                )}
                
                {userData.socialLinks.linkedin && (
                  <a 
                    href={userData.socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-300 hover:text-white transition-colors"
                  >
                    <svg className="h-5 w-5 text-[#ff1b6d] mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    <span className="truncate">LinkedIn</span>
                  </a>
                )}
                
                {userData.socialLinks.twitter && (
                  <a 
                    href={userData.socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-gray-300 hover:text-white transition-colors"
                  >
                    <svg className="h-5 w-5 text-[#ff1b6d] mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                    </svg>
                    <span className="truncate">Twitter</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProfessionalInfo;

